import { useState, useEffect, useCallback } from 'preact/hooks';
import type { ProjectPlanSummary } from '@voice-assistant/shared';
import { authFetch } from '../api/authFetch';

interface QueueStats {
  waiting: number;
  active: number;
  completed: number;
  failed: number;
  delayed: number;
  paused: boolean;
  todayCostUsd: number;
  dailyCapUsd: number;
}

const RISK_COLORS: Record<string, string> = {
  low: '#10b981',
  medium: '#f59e0b',
  high: '#ef4444',
};

const STATUS_ICONS: Record<string, string> = {
  pending: '○',
  'in-progress': '▶',
  done: '✓',
  failed: '✗',
  'needs-review': '?',
  deferred: '—',
};

function StatCard({ value, label, color }: { value: number | string; label: string; color?: string }) {
  return (
    <div class="stats-card">
      <div class="stats-card-value" style={color ? { color } : undefined}>{value}</div>
      <div class="stats-card-label">{label}</div>
    </div>
  );
}

function CostBar({ current, cap }: { current: number; cap: number }) {
  const pct = Math.min(100, Math.round((current / cap) * 100));
  const color = pct > 80 ? '#ef4444' : pct > 50 ? '#f59e0b' : '#10b981';
  return (
    <div class="workflows-cost-bar-wrap">
      <div class="workflows-cost-bar" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

function PlanRow({ plan }: { plan: ProjectPlanSummary }) {
  const pendingChunks = plan.chunks?.filter((c) => c.status === 'pending' || c.status === 'in-progress') ?? [];
  if (pendingChunks.length === 0) return null;
  const doneCount = plan.chunks?.filter((c) => c.status === 'done').length ?? 0;
  const totalCount = plan.chunks?.length ?? 0;

  return (
    <div class="workflows-plan-row">
      <div class="workflows-plan-header">
        <span class="workflows-plan-dot"
          style={{ background: plan.status === 'executing' ? '#3b82f6' : plan.status === 'stale' ? '#f59e0b' : '#10b981' }} />
        <span class="workflows-plan-key">{plan.projectKey}</span>
        <span class="workflows-plan-meta">v{plan.version} · {doneCount}/{totalCount} done</span>
      </div>
      <div class="workflows-chunk-list">
        {pendingChunks.slice(0, 5).map((chunk) => (
          <div key={chunk.id} class="workflows-chunk-item">
            <span class="workflows-chunk-icon">{STATUS_ICONS[chunk.status] ?? '○'}</span>
            <span class="workflows-chunk-title">{chunk.title}</span>
            <span class="workflows-chunk-risk"
              style={{ color: RISK_COLORS[chunk.riskLevel] ?? '#8888aa' }}>
              {chunk.riskLevel?.toUpperCase()}
            </span>
          </div>
        ))}
        {pendingChunks.length > 5 && (
          <div class="workflows-chunk-more">+{pendingChunks.length - 5} more pending</div>
        )}
      </div>
    </div>
  );
}

export function WorkflowsPage() {
  const [queueStats, setQueueStats] = useState<QueueStats | null>(null);
  const [plans, setPlans] = useState<ProjectPlanSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const [qRes, pRes] = await Promise.all([
        authFetch('/api/memos/queue/stats'),
        authFetch('/api/memos/plans/list'),
      ]);
      if (qRes.ok) setQueueStats(await qRes.json());
      if (pRes.ok) {
        const data = await pRes.json();
        setPlans(data.plans ?? []);
      }
      setLastRefresh(new Date());
    } catch {
      // graceful — show whatever was loaded
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 10000);
    return () => clearInterval(id);
  }, [fetchData]);

  const activePlans = plans.filter((p) =>
    p.chunks?.some((c) => c.status === 'pending' || c.status === 'in-progress'),
  );

  const costPct = queueStats
    ? Math.min(100, Math.round((queueStats.todayCostUsd / queueStats.dailyCapUsd) * 100))
    : 0;

  return (
    <div class="memos-page workflows-page">
      <div class="memos-header">
        <div class="memos-header-left">
          <a class="memos-back" href="/" title="Back">←</a>
          <h1>
            Wor<span style={{ color: '#3b9eff' }}>k</span>flows
          </h1>
          {queueStats?.paused && (
            <span class="workflows-paused-badge">paused</span>
          )}
        </div>
        <span class="memos-stats" onClick={fetchData} title="Refresh now">
          {lastRefresh
            ? `${lastRefresh.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })} ↻`
            : '↻'}
        </span>
      </div>

      <div class="memos-content">
        {/* Queue stats */}
        {queueStats && (
          <div class="workflows-section">
            <div class="stats-row">
              <StatCard value={queueStats.waiting} label="Waiting" color="#f59e0b" />
              <StatCard value={queueStats.active} label="Active" color="#3b82f6" />
              <StatCard value={queueStats.completed} label="Done" color="#10b981" />
              <StatCard value={queueStats.failed} label="Failed" color="#ef4444" />
            </div>
            <div class="workflows-cost-row">
              <span class="workflows-cost-label">
                Today: <strong>${queueStats.todayCostUsd.toFixed(2)}</strong>
                {' / '}${queueStats.dailyCapUsd.toFixed(2)} cap
                {' '}({costPct}%)
              </span>
              <CostBar current={queueStats.todayCostUsd} cap={queueStats.dailyCapUsd} />
            </div>
          </div>
        )}

        {/* Active plans with pending work */}
        {!loading && activePlans.length > 0 && (
          <div class="workflows-section">
            <div class="workflows-section-title">Active Plans</div>
            {activePlans.map((plan) => (
              <PlanRow key={plan.id} plan={plan} />
            ))}
          </div>
        )}

        {!loading && activePlans.length === 0 && (
          <div class="monitor-message">
            No plans with pending chunks.
            <br />
            <span class="workflows-empty-hint">
              Go to <a href="/memos" class="workflows-inline-link">Board</a> to generate and execute plans.
            </span>
          </div>
        )}

        {!loading && (
          <div class="monitor-hint">BullMQ · auto-refreshes every 10s</div>
        )}
      </div>
    </div>
  );
}
